// Professional Skills Data - Based on Resume
// Organized for recruiter visibility with specific technology keywords

// Helper function to calculate years of experience from start year
const calculateYears = (startYear) => {
  const currentYear = new Date().getFullYear()
  return currentYear - startYear
}

export const techLogos = {
  // Languages & Core Technologies
  'TypeScript': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg',
  'Node.js': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg',
  'PHP': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/php/php-original.svg',
  'JavaScript': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg',
  'React.js': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg',
  'HTML5': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/html5/html5-original.svg',
  'CSS3': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/css3/css3-original.svg',
  'AngularJS': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/angularjs/angularjs-original.svg',
  
  // Frameworks
  'Laravel': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/laravel/laravel-original.svg',
  'Express.js': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/express/express-original.svg',
  'CodeIgniter': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/codeigniter/codeigniter-plain.svg',
  'Symfony': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/symfony/symfony-original.svg',
  
  // AWS Services
  'AWS': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original.svg',
  
  // Databases
  'MySQL': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg',
  'MongoDB': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg',
  'Redis': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redis/redis-original.svg',
  'PostgreSQL': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg',
  'ElasticSearch': 'https://www.vectorlogo.zone/logos/elastic/elastic-icon.svg',
  
  // DevOps & Tools
  'Git': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg',
  'GitHub': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/github/github-original.svg',
  'Bitbucket': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/bitbucket/bitbucket-original.svg',
  'Docker': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg',
  'Jest': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jest/jest-plain.svg',
  
  // Payment
  'Stripe': 'https://www.vectorlogo.zone/logos/stripe/stripe-icon.svg',
  'PayPal': 'https://www.vectorlogo.zone/logos/paypal/paypal-icon.svg',
  
  // Monitoring
  'Datadog': 'https://www.vectorlogo.zone/logos/datadoghq/datadoghq-icon.svg',
  
  // Default
  'default': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/devicon/devicon-original.svg'
}

// Professional skills based on actual resume experience
export const professionalSkills = [
  {
    category: 'AWS Cloud Services',
    icon: '☁️',
    color: 'aws',
    priority: 1,
    items: [
      { name: 'AWS Lambda', level: 95, years: calculateYears(2019), certified: true },
      { name: 'Amazon S3', level: 95, years: calculateYears(2018) },
      { name: 'Amazon EC2', level: 90, years: calculateYears(2018) },
      { name: 'Amazon DynamoDB', level: 90, years: calculateYears(2020) },
      { name: 'Amazon RDS', level: 85, years: calculateYears(2019) },
      { name: 'Amazon Kinesis', level: 85, years: calculateYears(2021) },
      { name: 'Amazon EventBridge', level: 85, years: calculateYears(2021) },
      { name: 'Amazon Cognito', level: 85, years: calculateYears(2020) },
      { name: 'Amazon SQS/SNS', level: 90, years: calculateYears(2019) },
      { name: 'Amazon CloudWatch', level: 90, years: calculateYears(2019) },
      { name: 'AWS API Gateway', level: 85, years: calculateYears(2020) },
      { name: 'AWS Cost Optimization', level: 90, years: calculateYears(2021) },
      { name: 'AWS Reserved Instances', level: 85, years: calculateYears(2022) },
      { name: 'Serverless Framework', level: 90, years: calculateYears(2020) }
    ]
  },
  {
    category: 'Backend Development',
    icon: '⚙️',
    color: 'backend',
    priority: 2,
    items: [
      { name: 'Node.js', level: 95, years: calculateYears(2017), featured: true },
      { name: 'TypeScript', level: 95, years: calculateYears(2017), featured: true },
      { name: 'PHP', level: 95, years: calculateYears(2015), featured: true },
      { name: 'Laravel', level: 95, years: calculateYears(2017) },
      { name: 'NestJS', level: 90, years: calculateYears(2019) },
      { name: 'Express.js', level: 90, years: calculateYears(2019) },
      { name: 'RESTful APIs', level: 95, years: calculateYears(2015) },
      { name: 'Serverless Architecture', level: 90, years: calculateYears(2020) },
      { name: 'Microservices', level: 85, years: calculateYears(2020) },
      { name: 'Domain-Driven Design', level: 85, years: calculateYears(2022) },
      { name: 'SOLID Principles', level: 90, years: calculateYears(2019) },
      { name: 'Test-Driven Development', level: 90, years: calculateYears(2019) },
      { name: 'CodeIgniter', level: 75, years: calculateYears(2015) },
      { name: 'Symfony', level: 75, years: calculateYears(2015) }
    ]
  },
  {
    category: 'Frontend Development',
    icon: '🎨',
    color: 'frontend',
    priority: 3,
    items: [
      { name: 'React.js', level: 90, years: calculateYears(2019) },
      { name: 'JavaScript ES6+', level: 95, years: calculateYears(2015) },
      { name: 'TypeScript', level: 95, years: calculateYears(2017) },
      { name: 'HTML5', level: 95, years: calculateYears(2015) },
      { name: 'CSS3', level: 90, years: calculateYears(2015) },
      { name: 'AngularJS', level: 75, years: calculateYears(2016) },
      { name: 'Responsive Design', level: 85, years: calculateYears(2017) },
      { name: 'Frontend Performance', level: 85, years: calculateYears(2019) }
    ]
  },
  {
    category: 'Databases & Storage',
    icon: '🗄️',
    color: 'database',
    priority: 4,
    items: [
      { name: 'MySQL', level: 95, years: calculateYears(2015) },
      { name: 'PostgreSQL', level: 85, years: calculateYears(2018) },
      { name: 'DynamoDB', level: 90, years: calculateYears(2020) },
      { name: 'MongoDB', level: 85, years: calculateYears(2015) },
      { name: 'Redis', level: 85, years: calculateYears(2015) },
      { name: 'ElasticSearch', level: 85, years: calculateYears(2017) },
      { name: 'Database Optimization', level: 90, years: calculateYears(2017) },
      { name: 'Data Migration', level: 85, years: calculateYears(2019) }
    ]
  },
  {
    category: 'DevOps & CI/CD',
    icon: '🔧',
    color: 'devops',
    priority: 5,
    items: [
      { name: 'Git', level: 95, years: calculateYears(2015) },
      { name: 'GitHub', level: 95, years: calculateYears(2017) },
      { name: 'Bitbucket', level: 90, years: calculateYears(2017) },
      { name: 'CI/CD Pipelines', level: 90, years: calculateYears(2018) },
      { name: 'Docker', level: 90, years: calculateYears(2018) },
      { name: 'Kubernetes', level: 85, years: calculateYears(2018) },
      { name: 'Terraform', level: 85, years: calculateYears(2020) },
      { name: 'Jest Testing', level: 85, years: calculateYears(2021) },
      { name: 'Postman', level: 90, years: calculateYears(2019) },
      { name: 'Distelli', level: 75, years: calculateYears(2016) },
      { name: 'Infrastructure as Code', level: 80, years: calculateYears(2021) },
      { name: 'Automated Testing', level: 90, years: calculateYears(2019) }
    ]
  },
  {
    category: 'Monitoring & Observability',
    icon: '📊',
    color: 'monitoring',
    priority: 6,
    items: [
      { name: 'Datadog', level: 85, years: calculateYears(2021) },
      { name: 'CloudWatch', level: 90, years: calculateYears(2019) },
      { name: 'Application Monitoring', level: 85, years: calculateYears(2020) },
      { name: 'Performance Monitoring', level: 85, years: calculateYears(2020) },
      { name: 'Log Management', level: 85, years: calculateYears(2019) },
      { name: 'Alerting & Incident Response', level: 85, years: calculateYears(2020) }
    ]
  },
  {
    category: 'Payment Systems',
    icon: '💳',
    color: 'payment',
    priority: 7,
    items: [
      { name: 'Stripe Integration', level: 90, years: calculateYears(2021) },
      { name: 'PayPal Integration', level: 85, years: calculateYears(2021) },
      { name: 'RocketGate', level: 80, years: calculateYears(2021) },
      { name: 'InovioPay', level: 80, years: calculateYears(2021) },
      { name: 'Payment APIs', level: 90, years: calculateYears(2020) },
      { name: 'PCI Compliance', level: 85, years: calculateYears(2021) },
      { name: 'Revenue Processing', level: 85, years: calculateYears(2020) }
    ]
  },
  {
    category: 'Leadership & Management',
    icon: '👥',
    color: 'leadership',
    priority: 8,
    items: [
      { name: 'Agile/Scrum', level: 95, years: calculateYears(2017) },
      { name: 'Technical Leadership', level: 90, years: calculateYears(2018) },
      { name: 'Team Management', level: 90, years: calculateYears(2018) },
      { name: 'Code Reviews', level: 95, years: calculateYears(2017) },
      { name: 'Sprint Planning', level: 90, years: calculateYears(2018) },
      { name: 'OKR Definition', level: 85, years: calculateYears(2020) },
      { name: 'Mentoring', level: 90, years: calculateYears(2018) },
      { name: 'Stakeholder Management', level: 85, years: calculateYears(2019) },
      { name: 'Cross-functional Collaboration', level: 90, years: calculateYears(2017) },
      { name: 'Performance Reviews', level: 85, years: calculateYears(2020) }
    ]
  }
]

// Get all unique technologies for keyword optimization
export const getAllTechnologies = () => {
  const allTech = professionalSkills.flatMap(category => 
    category.items.map(item => ({
      ...item,
      category: category.category,
      priority: category.priority
    }))
  )
  
  return allTech.sort((a, b) => {
    // Sort by priority first, then by level
    if (a.priority !== b.priority) {
      return a.priority - b.priority
    }
    return b.level - a.level
  })
}

// Get featured/primary technologies
export const getFeaturedTechnologies = () => {
  return [
    {
      name: 'AWS Certified Solutions Architect',
      level: 95,
      years: calculateYears(2018),
      certified: true,
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original-wordmark.svg'
    },
    {
      name: 'Node.js / TypeScript / NestJS',
      level: 95,
      years: calculateYears(2017),
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg'
    },
    {
      name: 'PHP',
      level: 95,
      years: calculateYears(2015),
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/php/php-original.svg'
    },
    {
      name: 'Laravel',
      level: 95,
      years: calculateYears(2017),
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/laravel/laravel-original.svg'
    },
    {
      name: 'Docker',
      level: 90,
      years: calculateYears(2018),
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg'
    },
    {
      name: 'Kubernetes',
      level: 85,
      years: calculateYears(2018),
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kubernetes/kubernetes-plain.svg'
    },
    {
      name: 'Terraform',
      level: 85,
      years: calculateYears(2020),
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/terraform/terraform-original.svg'
    },
    {
      name: 'Serverless Architecture',
      level: 90,
      years: calculateYears(2020),
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original-wordmark.svg'
    },
    {
      name: 'React.js',
      level: 90,
      years: calculateYears(2019),
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg'
    },
    {
      name: 'MySQL',
      level: 95,
      years: calculateYears(2015),
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg'
    },
    {
      name: 'PostgreSQL',
      level: 85,
      years: calculateYears(2018),
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg'
    },
    {
      name: 'AI / RAG / LLM Integration',
      level: 85,
      years: calculateYears(2023),
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg'
    }
  ]
}

// Get technologies by experience level
export const getTechnologiesByExperience = () => {
  const allTech = getAllTechnologies()
  
  return {
    expert: allTech.filter(t => t.years >= 8), // 8+ years
    advanced: allTech.filter(t => t.years >= 5 && t.years < 8), // 5-7 years
    intermediate: allTech.filter(t => t.years >= 3 && t.years < 5), // 3-4 years
    learning: allTech.filter(t => t.years < 3) // Less than 3 years
  }
}

// Keywords for SEO and recruiter searches
export const technologyKeywords = [
  // Core
  'AWS Certified Solutions Architect',
  'Principal Software Engineer',
  'Technical Lead',
  'Full Stack Developer',
  
  // Languages
  'TypeScript', 'Node.js', 'JavaScript', 'PHP', 'React.js',
  
  // AWS
  'AWS Lambda', 'Amazon S3', 'Amazon EC2', 'Amazon DynamoDB',
  'Amazon Kinesis', 'Amazon EventBridge', 'Amazon Cognito',
  'Amazon SQS', 'Amazon SNS', 'CloudWatch', 'API Gateway',
  'Serverless', 'AWS Cost Optimization',
  
  // Frameworks
  'Laravel', 'Express.js', 'React', 'AngularJS',
  
  // Databases
  'MySQL', 'PostgreSQL', 'MongoDB', 'Redis', 'DynamoDB', 'ElasticSearch',
  
  // Methodologies
  'Agile', 'Scrum', 'Domain-Driven Design', 'Microservices',
  'Test-Driven Development', 'SOLID Principles',
  
  // Tools
  'Git', 'GitHub', 'Bitbucket', 'Docker', 'Jest', 'Postman',
  'CI/CD', 'Datadog',
  
  // Payment
  'Stripe', 'PayPal', 'Payment Integration',
  
  // Experience Level
  '10+ years experience', 'Cum Laude', 'Certified'
]